import { zodResolver } from '@hookform/resolvers/zod';
import AddBusinessRounded from '@mui/icons-material/AddBusinessRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import RestorePageRounded from '@mui/icons-material/RestorePageRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router';
import { z } from 'zod';
import { API_BASE, ApiError, type Schemas } from '../api/client';
import { AuthLayout } from './AuthLayout';
import { authButtonSx } from './styles';
import { useAuth } from './AuthProvider';

const schema = z
  .object({
    shopName: z.string().trim().min(2, 'Enter your shop name'),
    shopSlug: z
      .string()
      .trim()
      .min(3, 'At least 3 characters')
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and dashes only'),
    ownerName: z.string().trim().min(2, 'Enter your name'),
    email: z.union([z.literal(''), z.email('Enter a valid email')]),
    phone: z.string().trim(),
    password: z.string().min(8, 'At least 8 characters'),
  })
  .refine((v) => v.email || v.phone, {
    message: 'Enter an email or a phone number',
    path: ['email'],
  });
type Values = z.infer<typeof schema>;

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

type Start = 'new' | 'restore';

function StartChoice({ value, onChange }: { value: Start; onChange: (v: Start) => void }) {
  const options = [
    {
      id: 'new' as const,
      icon: <AddBusinessRounded />,
      title: 'Start a new shop',
      hint: 'Empty shop, free trial',
    },
    {
      id: 'restore' as const,
      icon: <RestorePageRounded />,
      title: 'Restore a backup',
      hint: 'From a PackPilot backup file',
    },
  ];
  return (
    <Stack
      direction="row"
      spacing={1.25}
      role="radiogroup"
      aria-label="How do you want to start?"
      sx={{ mb: 3 }}
    >
      {options.map((o) => {
        const on = value === o.id;
        return (
          <ButtonBase
            key={o.id}
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.id)}
            sx={(t) => ({
              position: 'relative',
              flex: 1,
              minWidth: 0,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              p: 1.75,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: on ? 'primary.main' : 'divider',
              bgcolor: on ? alpha(t.palette.primary.main, 0.05) : 'background.paper',
              boxShadow: on ? `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}` : 'none',
              textAlign: 'left',
              transition: 'border-color 140ms, background-color 140ms, box-shadow 140ms',
              '&:hover': { borderColor: on ? 'primary.main' : 'text.disabled' },
            })}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 14,
                right: 14,
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: on ? 'primary.main' : 'divider',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {on && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
              )}
            </Box>
            <Box
              sx={(t) => ({
                width: 34,
                height: 34,
                mb: 0.75,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                color: on ? 'primary.main' : 'text.secondary',
                bgcolor: on
                  ? alpha(t.palette.primary.main, 0.1)
                  : alpha(t.palette.text.primary, 0.05),
              })}
            >
              {o.icon}
            </Box>
            <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{o.title}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.5, pr: 1 }}>
              {o.hint}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}

const sizeText = (bytes: number) =>
  bytes >= 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;

type BackupCheck = Schemas['BackupCheck'];

/** The server checks the file first (signature, version) and says what it holds. */
const MAX_BACKUP_BYTES = 2 * 1024 ** 3;

async function checkBackup(file: File): Promise<BackupCheck> {
  if (file.size > MAX_BACKUP_BYTES) {
    throw new Error('This file is larger than a backup can be (2 GB).');
  }
  const res = await fetch(`${API_BASE}/auth/restore-shop/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: file,
  });
  if (res.status === 413) {
    throw new Error('This file is larger than a backup can be (2 GB).');
  }
  if (!res.ok) throw await ApiError.from(res);
  return (await res.json()) as BackupCheck;
}

const count = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** Picks the backup file and shows what the server found in it. */
function BackupPicker({
  file,
  checked,
  checking,
  problem,
  onPick,
}: {
  file: File | null;
  checked: BackupCheck | null;
  checking: boolean;
  problem: string | null;
  onPick: (f: File | null) => void;
}) {
  const tone = problem ? 'error' : checked ? 'success' : null;
  const detail = problem
    ? problem
    : checking
      ? 'Checking the file…'
      : checked
        ? `${checked.shop.name} · backed up ${new Date(checked.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · ${count(checked.counts.products, 'product')}, ${count(checked.counts.contacts, 'contact')}, ${count(checked.counts.salesInvoices, 'invoice')}`
        : file
          ? sizeText(file.size)
          : 'A .ppbackup file downloaded from Settings → Backup';
  return (
    <Box
      component="label"
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        borderRadius: '14px',
        border: '1.5px dashed',
        borderColor: tone ? `${tone}.main` : 'divider',
        bgcolor: tone ? alpha(t.palette[tone].main, 0.06) : 'background.paper',
        cursor: 'pointer',
        '&:hover': { borderColor: tone ? `${tone}.main` : 'primary.main' },
      })}
    >
      <input
        type="file"
        accept=".ppbackup"
        hidden
        onChange={(e) => {
          onPick(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />
      {checking ? (
        <CircularProgress size={22} />
      ) : (
        <RestorePageRounded sx={{ color: tone ? `${tone}.main` : 'text.secondary' }} />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
          {file ? file.name : 'Choose your backup file'}
        </Typography>
        <Typography color={problem ? 'error' : 'text.secondary'} sx={{ fontSize: 12.5 }}>
          {detail}
        </Typography>
      </Box>
      <Typography color="primary" sx={{ fontWeight: 600, fontSize: 13 }}>
        {file ? 'Change' : 'Browse'}
      </Typography>
    </Box>
  );
}

export function RegisterPage() {
  const { register: registerShop, restore } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState<Start>('new');
  const [backup, setBackup] = useState<File | null>(null);
  const [checked, setChecked] = useState<BackupCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    getFieldState,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      shopName: '',
      shopSlug: '',
      ownerName: '',
      email: '',
      phone: '',
      password: '',
    },
  });
  // Filled in from a backup without typing: the label must move up out of the way.
  const shopNameValue = useWatch({ control, name: 'shopName' });

  const pick = (file: File | null) => {
    setBackup(file);
    setChecked(null);
    setProblem(null);
    if (!file) return;
    setChecking(true);
    checkBackup(file)
      .then((result) => {
        setChecked(result);
        if (!getFieldState('shopName').isDirty) {
          setValue('shopName', result.shop.name);
          if (!getFieldState('shopSlug').isDirty) {
            setValue('shopSlug', `${result.shop.slug.slice(0, 32)}-restored`);
          }
        }
      })
      .catch((e: unknown) =>
        setProblem(
          e instanceof ApiError || e instanceof Error
            ? e.message
            : 'Could not check the file. Try again.',
        ),
      )
      .finally(() => setChecking(false));
  };

  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    const fields = {
      shopName: v.shopName,
      shopSlug: v.shopSlug,
      ownerName: v.ownerName,
      password: v.password,
      ...(v.email ? { email: v.email } : {}),
      ...(v.phone ? { phone: v.phone } : {}),
    };
    try {
      if (start === 'restore') {
        if (!checked) {
          setError(checking ? 'Wait until the file is checked.' : 'Choose your backup file first.');
          return;
        }
        await restore({ uploadId: checked.uploadId, ...fields });
      } else {
        await registerShop(fields);
      }
      void navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'BACKUP_EXPIRED') {
        setChecked(null);
        setBackup(null);
      }
      setError(
        e instanceof ApiError && e.code === 'SLUG_TAKEN'
          ? 'That shop ID is already taken. Try another.'
          : e instanceof ApiError
            ? e.message
            : 'Could not reach the server. Please try again.',
      );
    }
  });

  const shopName = register('shopName');

  return (
    <AuthLayout
      panel="signup"
      back={
        <Button
          component={RouterLink}
          to="/login"
          variant="text"
          size="small"
          startIcon={<ArrowBackRounded />}
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Back to sign in
        </Button>
      }
      title={start === 'new' ? 'Create your shop' : 'Restore your shop'}
      subtitle={
        <>
          {start === 'new'
            ? 'Start your free trial in under a minute.'
            : 'Bring a backup into a new shop: products, customers, documents and stock.'}{' '}
          Already registered?{' '}
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
            Sign in
          </Link>
        </>
      }
    >
      <StartChoice value={start} onChange={setStart} />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}
          {start === 'restore' && (
            <BackupPicker
              file={backup}
              checked={checked}
              checking={checking}
              problem={problem}
              onPick={pick}
            />
          )}
          <TextField
            size="medium"
            label="Shop name"
            autoFocus
            {...shopName}
            slotProps={shopNameValue ? { inputLabel: { shrink: true } } : undefined}
            onChange={(e) => {
              void shopName.onChange(e);
              if (!getFieldState('shopSlug').isDirty) setValue('shopSlug', slugify(e.target.value));
            }}
            error={Boolean(errors.shopName)}
            helperText={errors.shopName?.message}
          />
          <TextField
            size="medium"
            label="Shop ID"
            {...register('shopSlug')}
            error={Boolean(errors.shopSlug)}
            helperText={errors.shopSlug?.message ?? 'You and your staff use this to sign in.'}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">@</InputAdornment> },
            }}
          />
          <TextField
            size="medium"
            label="Your name"
            autoComplete="name"
            {...register('ownerName')}
            error={Boolean(errors.ownerName)}
            helperText={errors.ownerName?.message}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="medium"
              label="Email"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
            <TextField label="Phone" autoComplete="tel" {...register('phone')} />
          </Stack>
          <TextField
            size="medium"
            label="Password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
            error={Boolean(errors.password)}
            helperText={errors.password?.message ?? 'At least 8 characters.'}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={isSubmitting}
            fullWidth
            sx={authButtonSx}
          >
            {start === 'new'
              ? 'Create shop'
              : isSubmitting
                ? 'Restoring your shop…'
                : 'Restore shop'}
          </Button>
          {start === 'restore' && (
            <Typography color="text.secondary" sx={{ fontSize: 12.5, textAlign: 'center' }}>
              You become the owner of the new shop. Staff from the backup are restored and set a new
              password on their first sign-in.
            </Typography>
          )}
        </Stack>
      </form>
    </AuthLayout>
  );
}
